// The person tab (design handoff §6, D1-c §3.2).
//
// Two columns: the projects own the screen on the left, a 392px sticky Today
// rail fills up on the right. Pulling is a short gesture and the day's list
// stays legible while the driver walks the cards.
//
// The rules this surface exists to enforce:
//
//   · **A focus is a pin, not a copy.** Ticking in the rail writes through to
//     the register row — `POST /standup/pin-done` closes the brain action, and
//     the card shows `Done` without a reload.
//   · **No text input in the rail, ever.** A one-off is quick-added into a
//     project register (Support is always a valid home) and then pulled. The
//     absence is stated in a footnote, not left as a suspicious gap.
//   · **Support is last, and only for people who have tickets.** The server
//     decides that; this file just renders `data.support` at the end of
//     WORKING ON.
//   · **Four reconciliation outcomes**, not the handoff's three. Park leaves
//     Today, stays open on the project, and records a parked-since date.
//
// Every write goes through the standup routes so it survives a reload, and
// every reconciliation lands in the day's transcript. A reconciliation click
// also gets a live ~5s Undo window before the write fires at all (see
// `resolve`/`pendingResolves` below, added 2026-08-13 per /impeccable critique)
// — the transcript is the durable record, the Undo window is the fat-finger
// recovery for the one moment on this page with no other safety net.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../design/brainTheme";
import { Spinner } from "../../design/ui";
import {
  getPersonProjects,
  putStandupFocuses,
  putStandupRunPins,
  reconcilePin,
  setPinDone,
  type BoardMember,
  type Focus,
  type FocusResolution,
  type ParkedPin,
  type PersonProjectCard as CardData,
  type PersonProjects,
} from "../../lib/api";
import type { ProjectAction } from "../../lib/brain-api";
import {
  PersonProjectCard,
  ReconciliationStrip,
  TodayRail,
  RESOLUTION_LABEL,
  buildReconRows,
  focusFromRow,
  personKeys as buildPersonKeys,
  pinMatchesRow,
  Icon,
  PersonAvatar,
  StatusPill,
  projectStatusMeta,
  type ActionRegisterPerson,
  type RegisterPull,
  type RegisterRow,
  type ReconRow,
} from "../../mui";

const c = brainTokens.color;

/** Rail width is fixed by the handoff — the itinerary must not reflow. */
const RAIL_WIDTH = 392;

// Session cache: walking the room means flicking between person tabs, and the
// route behind this costs seconds per person. Show the last snapshot instantly
// and refresh it in the background. Keyed by person+date.
const personCache = new Map<string, PersonProjects>();

function GroupHeading({ label, hint }: { label: string; hint: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 1.4 }}>
      <Box
        component="span"
        sx={{
          fontFamily: brainTokens.font.mono,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.11em",
          color: c.muted2,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
      <Box component="span" sx={{ fontSize: 13, color: c.muted }}>
        {hint}
      </Box>
    </Box>
  );
}

/** Handoff §7: owning nothing is a real state, not an error. Say so. */
function NoOwnedProjects({ personName }: { personName: string }) {
  return (
    <Box
      data-testid="no-owned-projects"
      sx={{
        border: `1px dashed ${c.dropBorder}`,
        background: c.white,
        borderRadius: `${brainTokens.radius.card}px`,
        p: "22px",
      }}
    >
      <Box sx={{ fontSize: 15, fontWeight: 650, color: c.ink }}>{personName} owns no projects.</Box>
    </Box>
  );
}

export function PersonProjectsTab({
  member,
  date,
  canEdit,
  people = [],
  onSaved,
}: {
  member: BoardMember;
  date: string;
  canEdit: boolean;
  people?: ActionRegisterPerson[];
  /** Ask the page to reload the board (focuses, yesterday, carried). */
  onSaved: () => void;
}) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [data, setData] = useState<PersonProjects | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [focuses, setFocuses] = useState<Focus[]>(member.focuses ?? []);
  const [busy, setBusy] = useState(false);
  const [resolved, setResolved] = useState<Record<string, FocusResolution>>({});
  const [reconRows, setReconRows] = useState<ReconRow[]>([]);
  const [ghost, setGhost] = useState<{ text: string; ref?: string } | null>(null);
  const [dragging, setDragging] = useState(false);
  // Write-through display state: a tick closes the register row server-side, and
  // the card must show it WITHOUT a reload (the pin-not-a-copy acceptance test).
  const [actionOverrides, setActionOverrides] = useState<Record<string, Partial<ProjectAction>>>({});
  const dragRow = useRef<{ row: RegisterRow; projectId: string } | null>(null);
  // The rail's own list is the source of truth for pin writes; keep it in a ref
  // so two quick clicks never race on a stale closure.
  const focusesRef = useRef<Focus[]>(focuses);
  focusesRef.current = focuses;

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    const cached = personCache.get(`${member.userId}|${date}`);
    // Cached snapshot renders immediately; the fetch below silently replaces it.
    setLoading(!cached);
    setLoadError(null);
    setData(cached ?? null);
    getPersonProjects(member.userId, date || undefined, ctrl.signal)
      .then((d) => {
        personCache.set(`${member.userId}|${date}`, d);
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled && e?.name !== "AbortError") {
          setLoadError(e instanceof Error ? e.message : "Failed to load this person's projects.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [member.userId, date]);

  // Switching person or day starts a fresh ceremony.
  useEffect(() => {
    setResolved({});
    setReconRows([]);
    setActionOverrides({});
  }, [member.userId, date]);

  // Board polling re-supplies focuses; local edits are written straight through,
  // so taking the server's copy is always safe here.
  useEffect(() => {
    setFocuses(member.focuses ?? []);
  }, [member.focuses]);

  const cards = useMemo(() => {
    if (!data) return [] as CardData[];
    return [...data.owner, ...data.working, ...(data.support ? [data.support] : [])];
  }, [data]);

  const nameOf = useCallback(
    (projectId?: string) => {
      if (!projectId) return "No project";
      return cards.find((p) => p.projectId === projectId)?.name ?? projectId;
    },
    [cards],
  );

  // Reconciliation rows are ADDITIVE for the session: a row the driver has just
  // resolved must keep its confirmation chip at 62% rather than vanishing the
  // instant the board poll catches up. The strip hides itself when the last one
  // is resolved — that is the only "done" signal the ceremony needs.
  const computedRecon = useMemo(() => buildReconRows(member, nameOf), [member, nameOf]);
  useEffect(() => {
    setReconRows((prev) => {
      const byId = new Map(prev.map((r) => [r.focus.id, r] as const));
      let added = false;
      for (const r of computedRecon) {
        if (!byId.has(r.focus.id)) {
          byId.set(r.focus.id, r);
          added = true;
        }
      }
      return added ? [...byId.values()] : prev;
    });
  }, [computedRecon]);

  const personName = data?.displayName?.split(/\s+/)[0] ?? member.displayName.split(/\s+/)[0];
  const keys = useMemo(
    () =>
      buildPersonKeys({
        displayName: data?.displayName ?? member.displayName,
        ...(data?.personSlug ? { personSlug: data.personSlug } : {}),
      }),
    [data?.displayName, data?.personSlug, member.displayName],
  );
  const parkedByProject = useMemo(() => {
    const map: Record<string, ParkedPin[]> = {};
    for (const p of data?.parked ?? []) {
      const key = p.projectId ?? "";
      (map[key] ||= []).push(p);
    }
    return map;
  }, [data?.parked]);

  // ---- focus list ----------------------------------------------------------
  // Membership is derived; ATTENTION is not. The registry still holds shell
  // projects nobody can walk, so the tab shows this person's focus list and
  // keeps the derived remainder one click away in the picker. Same profile
  // field the run rail pins with — one focus list per person, never two.
  const [pinned, setPinned] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => {
    setPinned(data?.pinnedProjectIds ?? []);
  }, [data?.pinnedProjectIds]);

  const toggleFocus = useCallback(
    (projectId: string, next: boolean) => {
      if (!canEdit) {
        enqueueSnackbar("You can only change your own focus list.", { variant: "warning" });
        return;
      }
      const prev = pinned;
      const ids = next ? [...prev.filter((x) => x !== projectId), projectId] : prev.filter((x) => x !== projectId);
      setPinned(ids);
      void putStandupRunPins(member.userId, ids).catch((e) => {
        setPinned(prev);
        enqueueSnackbar(e instanceof Error ? e.message : "Could not save the focus list.", {
          variant: "error",
        });
      });
    },
    [canEdit, pinned, member.userId, enqueueSnackbar],
  );

  const persist = useCallback(
    async (next: Focus[], note?: string) => {
      const prev = focusesRef.current;
      setFocuses(next);
      setBusy(true);
      try {
        const saved = await putStandupFocuses(member.userId, date, next);
        setFocuses(saved);
        if (note) enqueueSnackbar(note, { variant: "success" });
        onSaved();
      } catch (e) {
        setFocuses(prev);
        enqueueSnackbar(e instanceof Error ? e.message : "Could not save today's list.", {
          variant: "error",
        });
      } finally {
        setBusy(false);
      }
    },
    [member.userId, date, enqueueSnackbar, onSaved],
  );

  const isPinned = useCallback(
    (row: RegisterRow) => focuses.some((f) => pinMatchesRow(f, row)),
    [focuses],
  );

  const togglePull = useCallback(
    (row: RegisterRow, projectId: string) => {
      if (!canEdit) {
        enqueueSnackbar("You can only pull work onto your own day.", { variant: "warning" });
        return;
      }
      const current = focusesRef.current;
      const hit = current.find((f) => pinMatchesRow(f, row));
      if (hit) {
        void persist(current.filter((f) => f.id !== hit.id));
      } else {
        void persist([...current, focusFromRow(row, projectId, date)]);
      }
    },
    [canEdit, date, persist, enqueueSnackbar],
  );

  /**
   * Bulk pull — ONE write for the whole batch. Looping `togglePull` would fire a
   * PUT per row against the same focus list and the later writes would clobber
   * the earlier ones; the list is replaced wholesale, not appended to.
   */
  const pullMany = useCallback(
    (rows: RegisterRow[], projectId: string) => {
      if (!canEdit) {
        enqueueSnackbar("You can only pull work onto your own day.", { variant: "warning" });
        return;
      }
      const current = focusesRef.current;
      const additions = rows
        .filter((row) => !current.some((f) => pinMatchesRow(f, row)))
        .map((row) => focusFromRow(row, projectId, date));
      if (!additions.length) {
        enqueueSnackbar("Everything showing is already on today.", { variant: "info" });
        return;
      }
      void persist(
        [...current, ...additions],
        `Pulled ${additions.length} item${additions.length === 1 ? "" : "s"} onto today.`,
      );
    },
    [canEdit, date, persist, enqueueSnackbar],
  );

  const pullFor = useCallback(
    (projectId: string): RegisterPull => ({
      isPinned,
      busy: busy || !canEdit,
      onToggle: (row) => togglePull(row, projectId),
      onPullMany: (rows) => pullMany(rows, projectId),
      onDragStart: (row) => {
        dragRow.current = { row, projectId };
        setDragging(true);
        setGhost({ text: row.summary, ...(row.issueKey ? { ref: row.issueKey } : {}) });
      },
      onDragEnd: () => {
        dragRow.current = null;
        setDragging(false);
        setGhost(null);
      },
      onHover: (row) => {
        if (dragRow.current) return; // a live drag owns the ghost
        if (!row || isPinned(row)) setGhost(null);
        else setGhost({ text: row.summary, ...(row.issueKey ? { ref: row.issueKey } : {}) });
      },
    }),
    [isPinned, busy, canEdit, togglePull, pullMany],
  );

  const onDropPin = useCallback(() => {
    const dragged = dragRow.current;
    dragRow.current = null;
    setDragging(false);
    setGhost(null);
    if (!dragged) return;
    if (focusesRef.current.some((f) => pinMatchesRow(f, dragged.row))) return;
    togglePull(dragged.row, dragged.projectId);
  }, [togglePull]);

  /** Tick a pin in the rail — writes THROUGH to the register row it names. */
  const tick = useCallback(
    async (focus: Focus, done: boolean) => {
      if (!canEdit) {
        enqueueSnackbar("You can only tick your own focuses.", { variant: "warning" });
        return;
      }
      const prev = focusesRef.current;
      setFocuses(prev.map((f) => (f.id === focus.id ? { ...f, done } : f)));
      setBusy(true);
      try {
        const res = await setPinDone({ userId: member.userId, date, focusId: focus.id, done });
        if (res.focuses) setFocuses(res.focuses);
        if (focus.actionId) {
          setActionOverrides((m) => ({
            ...m,
            [focus.actionId!]: done
              ? { status: "done", clientStatus: "Done" }
              : { status: "captured", clientStatus: undefined },
          }));
        }
        if (res.warning) enqueueSnackbar(res.warning, { variant: "warning", autoHideDuration: 8000 });
        onSaved();
      } catch (e) {
        setFocuses(prev);
        enqueueSnackbar(e instanceof Error ? e.message : "Could not update the pin.", {
          variant: "error",
        });
      } finally {
        setBusy(false);
      }
    },
    [canEdit, member.userId, date, enqueueSnackbar, onSaved],
  );

  const returnPin = useCallback(
    (focus: Focus) => {
      if (!canEdit) {
        enqueueSnackbar("You can only edit your own focuses.", { variant: "warning" });
        return;
      }
      void persist(
        focusesRef.current.filter((f) => f.id !== focus.id),
        "Returned to the register.",
      );
    },
    [canEdit, persist, enqueueSnackbar],
  );

  /** The actual write-through. Used directly by carryAll (already-confirmed
   *  bulk action); wrapped by `resolve` below with an undo grace window for
   *  the single-row buttons, where a fat-fingered click has no other recovery
   *  path (D3 critique finding). */
  const commitResolve = useCallback(
    async (row: ReconRow, resolution: FocusResolution) => {
      setBusy(true);
      try {
        const res = await reconcilePin({
          userId: member.userId,
          date,
          fromDate: row.date,
          focusId: row.focus.id,
          resolution,
        });
        if (res.focuses) setFocuses(res.focuses);
        if (resolution === "done" && row.focus.actionId) {
          setActionOverrides((m) => ({
            ...m,
            [row.focus.actionId!]: { status: "done", clientStatus: "Done" },
          }));
        }
        if (res.warning) enqueueSnackbar(res.warning, { variant: "warning", autoHideDuration: 8000 });
        onSaved();
      } catch (e) {
        setResolved((m) => {
          const next = { ...m };
          delete next[row.focus.id];
          return next;
        });
        enqueueSnackbar(e instanceof Error ? e.message : "Could not resolve that item.", {
          variant: "error",
        });
      } finally {
        setBusy(false);
      }
    },
    [member.userId, date, enqueueSnackbar, onSaved],
  );

  // focusId → the pending grace-window write, so Undo can cancel it and so an
  // unmount mid-window flushes it instead of silently dropping the action.
  const pendingResolves = useRef<Record<string, { timer: ReturnType<typeof setTimeout>; commit: () => void }>>({});
  useEffect(() => {
    const pending = pendingResolves.current;
    return () => {
      Object.values(pending).forEach(({ timer, commit }) => {
        clearTimeout(timer);
        commit();
      });
    };
  }, []);

  const resolve = useCallback(
    (row: ReconRow, resolution: FocusResolution) => {
      if (!canEdit) {
        enqueueSnackbar("You can only reconcile your own focuses.", { variant: "warning" });
        return;
      }
      // Optimistic: the row shows resolved immediately. The actual write is
      // held for a few seconds so Undo can cancel it before anything happens —
      // no backend "unresolve" needed, since nothing has been sent yet.
      setResolved((m) => ({ ...m, [row.focus.id]: resolution }));

      const commit = () => {
        delete pendingResolves.current[row.focus.id];
        void commitResolve(row, resolution);
      };
      const snackbarId = enqueueSnackbar(`${RESOLUTION_LABEL[resolution]} — ${row.focus.text.slice(0, 48)}`, {
        autoHideDuration: 5000,
        action: (key) => (
          <Button
            size="small"
            sx={{ color: "inherit", fontWeight: 600 }}
            onClick={() => {
              const pending = pendingResolves.current[row.focus.id];
              if (pending) {
                clearTimeout(pending.timer);
                delete pendingResolves.current[row.focus.id];
              }
              setResolved((m) => {
                const next = { ...m };
                delete next[row.focus.id];
                return next;
              });
              closeSnackbar(key);
            }}
          >
            Undo
          </Button>
        ),
      });
      pendingResolves.current[row.focus.id] = { timer: setTimeout(commit, 5000), commit };
    },
    [canEdit, enqueueSnackbar, closeSnackbar, commitResolve],
  );

  const carryAll = useCallback(async () => {
    for (const row of reconRows) {
      if (resolved[row.focus.id]) continue;
      setResolved((m) => ({ ...m, [row.focus.id]: "carry" }));
      // Sequential on purpose: each carry appends to the SAME today row, and
      // parallel appends would race and lose pins. "Carry all" is already an
      // explicit bulk confirmation, so it commits immediately — no undo window.
      // eslint-disable-next-line no-await-in-loop
      await commitResolve(row, "carry");
    }
  }, [reconRows, resolved, commitResolve]);

  const applyOverrides = useCallback(
    (list: ProjectAction[] | null) =>
      list?.map((a) => (actionOverrides[a.id] ? { ...a, ...actionOverrides[a.id] } : a)) ?? null,
    [actionOverrides],
  );

  if (loading) return <Spinner label="Loading this person's projects…" />;

  if (loadError || !data) {
    return (
      <Box
        sx={{
          border: `1px solid ${c.border}`,
          background: c.white,
          borderRadius: `${brainTokens.radius.card}px`,
          p: "22px",
        }}
      >
        <Box sx={{ fontSize: 15, fontWeight: 650, color: c.ink }}>
          Couldn’t load {member.displayName}’s projects.
        </Box>
        <Box sx={{ fontSize: 13.5, color: c.muted, mt: 0.75 }}>{loadError}</Box>
      </Box>
    );
  }

  const pinnedSet = new Set(pinned);
  const focusCards = pinned
    .map((id) => cards.find((p) => p.projectId === id))
    .filter((p): p is CardData => !!p);
  const restOwner = data.owner.filter((p) => !pinnedSet.has(p.projectId));
  const restWorking = [...data.working, ...(data.support ? [data.support] : [])].filter(
    (p) => !pinnedSet.has(p.projectId),
  );
  const restCount = restOwner.length + restWorking.length;

  const renderCard = (card: CardData) => (
    <PersonProjectCard
      key={card.projectId}
      card={{ ...card, focused: pinnedSet.has(card.projectId) }}
      personName={personName}
      personKeys={keys}
      {...(data.jiraAccountId ? { jiraAccountId: data.jiraAccountId } : {})}
      parked={parkedByProject[card.projectId] ?? []}
      nzDate={date}
      people={people}
      canEdit={canEdit}
      pull={pullFor(card.projectId)}
      overrideActions={applyOverrides}
    />
  );

  /** One project in the picker: enough to decide, nothing to walk. */
  const renderPickerRow = (card: CardData) => {
    const on = pinnedSet.has(card.projectId);
    const status = card.status ? projectStatusMeta(card.status) : null;
    return (
      <Box
        key={card.projectId}
        component={canEdit ? "button" : "div"}
        type={canEdit ? "button" : undefined}
        onClick={canEdit ? () => toggleFocus(card.projectId, !on) : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          width: "100%",
          p: "10px 12px",
          border: `1px solid ${on ? c.tealTintBorder : c.border}`,
          borderRadius: `${brainTokens.radius.card}px`,
          background: on ? c.tealTint2 : c.white,
          textAlign: "left",
          font: "inherit",
          color: "inherit",
          cursor: canEdit ? "pointer" : "default",
          "&:hover": canEdit ? { borderColor: c.hoverBorder } : undefined,
        }}
      >
        <Box
          component="span"
          sx={{ flex: "none", lineHeight: 0, color: on ? c.tealInk : c.placeholder }}
        >
          <Icon name="star" size={16} fill={on ? "currentColor" : "none"} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ fontSize: 14, fontWeight: 600, color: c.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {card.name}
          </Box>
          <Box sx={{ fontSize: 12, color: c.muted, mt: 0.2 }}>
            {card.customer && card.customer !== "aeronet" ? `${card.customer} · ` : ""}
            {card.counts.open} open · {card.counts.yours} theirs
            {card.counts.blocked > 0 ? ` · ${card.counts.blocked} blocked` : ""}
          </Box>
        </Box>
        {status && <StatusPill label={status.word} tone={status.tone} />}
        {card.owner && <PersonAvatar name={card.owner} size={24} />}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0,1fr)", lg: `minmax(0,1fr) ${RAIL_WIDTH}px` },
        gap: 2.5,
        alignItems: "start",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, mb: 1.4 }}>
          <GroupHeading
            label="FOCUS"
            hint={
              focusCards.length
                ? `${focusCards.length} project${focusCards.length === 1 ? "" : "s"} · what ${personName} walks today`
                : "nothing focused yet"
            }
          />
          <Button
            size="small"
            onClick={() => setPickerOpen(true)}
            startIcon={<Icon name="star" size={14} />}
            sx={{ flex: "none", color: c.tealInk }}
          >
            All projects ({cards.length})
          </Button>
        </Box>

        {focusCards.length === 0 ? (
          <Box
            data-testid="no-focus-projects"
            sx={{
              border: `1px dashed ${c.dropBorder}`,
              background: c.white,
              borderRadius: `${brainTokens.radius.card}px`,
              p: "22px",
              mb: 2,
            }}
          >
            <Box sx={{ fontSize: 15, fontWeight: 650, color: c.ink }}>
              No projects focused for {data.displayName}.
            </Box>
            {restCount > 0 && (
              <Box sx={{ fontSize: 13.5, color: c.muted, mt: 0.75, lineHeight: 1.6 }}>
                Star the few worth walking at standup.
              </Box>
            )}
            {restCount > 0 && (
              <Button variant="contained" size="small" sx={{ mt: 1.75 }} onClick={() => setPickerOpen(true)}>
                Choose projects
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 2 }}>
            {focusCards.map(renderCard)}
          </Box>
        )}

        {data.owner.length === 0 && focusCards.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <NoOwnedProjects personName={data.displayName} />
          </Box>
        )}

      </Box>

      <Box
        sx={{
          position: { lg: "sticky" },
          top: 16,
          alignSelf: "start",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          width: { xs: "100%", lg: RAIL_WIDTH },
        }}
      >
        <ReconciliationStrip
          rows={reconRows}
          today={date}
          resolved={resolved}
          busy={busy}
          onResolve={(row, resolution) => void resolve(row, resolution)}
          onCarryAll={() => void carryAll()}
        />
        <TodayRail
          focuses={focuses}
          today={date}
          personName={personName}
          projectName={nameOf}
          dragging={dragging}
          ghost={ghost}
          busy={busy}
          onTick={(focus, done) => void tick(focus, done)}
          onReturn={returnPin}
          onDropPin={onDropPin}
        />
      </Box>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          {data.displayName}’s projects
          <Box sx={{ fontSize: 13, color: c.muted, fontWeight: 400, mt: 0.5 }}>
            Starred projects show on this tab.
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {focusCards.length > 0 && (
            <>
              <GroupHeading label="FOCUS" hint={`${focusCards.length} shown on the tab`} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 2.5 }}>
                {focusCards.map(renderPickerRow)}
              </Box>
            </>
          )}
          <GroupHeading
            label="OWNER OF"
            hint={`${restOwner.length} not focused · they drive the deliverable`}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 2.5 }}>
            {restOwner.length ? (
              restOwner.map(renderPickerRow)
            ) : (
              <Box sx={{ fontSize: 13, color: c.muted }}>Nothing left — every owned project is focused.</Box>
            )}
          </Box>
          <GroupHeading label="WORKING ON" hint={`${restWorking.length} not focused · they have open rows`} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, pb: 1 }}>
            {restWorking.length ? (
              restWorking.map(renderPickerRow)
            ) : (
              <Box sx={{ fontSize: 13, color: c.muted }}>
                Nothing assigned to {personName} on someone else’s project.
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
